import * as applicationHooks from "@rentiohq/shared-frontend/dist/reduxV2/applications/application.hooks";
import * as React from "react";
import {
  clearSeenCounts,
  getNewCountsPerTab,
  readSeenCounts,
  shouldSeedBaseline,
  TCandidacyTabId,
  TStepCounts,
  withTabMarkedSeen,
  writeSeenCounts,
} from "./newCandidacies.utils";

/**
 * Notices that candidacies arrived, without fetching them.
 *
 * `count-steps` is one indexed query scoped to this property and about 90 bytes
 * back. The list is about 5 kB per candidacy and four extra queries, so this
 * asks the cheap question often and leaves the expensive one to the moment the
 * broker asks for it (DEV-3272).
 *
 * Nothing here touches the list. It reports numbers; the screen decides what to
 * do with them.
 */

/** Slow on purpose: this is for the broker watching a screen, not a live feed. */
const POLL_INTERVAL_MS = 60_000;

export const useNewCandidacies = (propertyId: number | undefined) => {
  const query = React.useMemo(
    () =>
      propertyId ? { filter: { propertyId: { eq: propertyId } } } : undefined,
    [propertyId],
  );

  const { countSteps, refetch } = applicationHooks.useCountSteps({
    query,
    shouldRefetch: true,
  });

  const currentCounts: TStepCounts | undefined = countSteps?.steps;

  // The counts this browser last showed for this property. Read once: after
  // that this state is the truth and storage only mirrors it, so a second
  // window on the same property cannot pull the numbers out from under this one
  // halfway through.
  const [seenCounts, setSeenCounts] = React.useState<TStepCounts | undefined>(
    undefined,
  );

  // The counts as they stood when the broker last moved something themselves.
  // Held so the re-seed below can tell the answer from before that action
  // apart from the one that comes back after it (DEV-3272).
  const countsBeforeOwnAction = React.useRef<TStepCounts | undefined>(
    undefined,
  );

  React.useEffect(() => {
    if (!propertyId) {
      return;
    }

    setSeenCounts(readSeenCounts(propertyId)?.seenCounts);
  }, [propertyId]);

  // A first visit stores what is there and shows nothing, so opening a property
  // for the first time never greets the broker with a number for every phase.
  // The same path re-seeds the baseline after the broker's own action threw it
  // away, which is why it asks the state rather than storage for "do we have
  // one": forgetting is expressed by clearing both.
  React.useEffect(() => {
    if (
      !propertyId ||
      !shouldSeedBaseline(
        currentCounts,
        seenCounts,
        countsBeforeOwnAction.current,
      )
    ) {
      return;
    }

    countsBeforeOwnAction.current = undefined;
    writeSeenCounts(propertyId, currentCounts as TStepCounts);
    setSeenCounts(currentCounts);
  }, [propertyId, currentCounts, seenCounts]);

  // Asked again while the broker is actually looking, and never while the
  // browser tab is in the background: a screen nobody is watching does not need
  // to know, and a stack of open tabs should not each keep a query running.
  React.useEffect(() => {
    if (!propertyId) {
      return;
    }

    const askIfVisible = () => {
      if (document.visibilityState === "visible") {
        refetch();
      }
    };

    const interval = setInterval(askIfVisible, POLL_INTERVAL_MS);
    window.addEventListener("focus", askIfVisible);
    document.addEventListener("visibilitychange", askIfVisible);

    return () => {
      clearInterval(interval);
      window.removeEventListener("focus", askIfVisible);
      document.removeEventListener("visibilitychange", askIfVisible);
    };
  }, [propertyId, refetch]);

  const newCountsPerTab = React.useMemo(
    () => getNewCountsPerTab(currentCounts, seenCounts),
    [currentCounts, seenCounts],
  );

  /**
   * The broker is now looking at this tab, so its number is spent. Every other
   * tab keeps what it had, because those have still not been looked at.
   */
  const markTabSeen = React.useCallback(
    (tabId: TCandidacyTabId) => {
      if (!propertyId || !currentCounts) {
        return;
      }

      setSeenCounts(previous => {
        const next = withTabMarkedSeen(previous, currentCounts, tabId);
        if (next) {
          writeSeenCounts(propertyId, next);
        }
        return next;
      });
    },
    [propertyId, currentCounts],
  );

  /**
   * The broker just changed something themselves, so nothing on this screen is
   * news to them any more.
   *
   * Cancelling a visit moves a candidacy from the visitors tab back to the
   * invited one. The row
   * arrives on the other tab at once, because the list reads through the same
   * cache the action patches, but the counts this browser remembers still
   * described the world before the click. The next check then reported that
   * arrival as something new and put a "+1" on a tab the broker had just filled
   * themselves.
   *
   * The baseline is forgotten rather than guessed at. The counts that would
   * replace it are a round trip away, and any local arithmetic would have to
   * reproduce what the phase move did to every tab, including the moves the
   * server makes of its own accord, such as sending others to the waiting
   * list. Forgotten is also the safe state: no baseline means no numbers at
   * all.
   *
   * Forgetting alone was not enough, which is what DEV-3272 came back for. The
   * counts in hand at this moment were fetched before the move, so re-seeding
   * from them wrote the old world back as the baseline, and the answer landing
   * a moment later then read as an arrival. They are kept here so the re-seed
   * can wait for numbers that actually differ.
   */
  const forgetBaseline = React.useCallback(() => {
    if (!propertyId) {
      return;
    }

    countsBeforeOwnAction.current = currentCounts;
    clearSeenCounts(propertyId);
    setSeenCounts(undefined);
    refetch();
  }, [propertyId, currentCounts, refetch]);

  return {
    newCountsPerTab,
    markTabSeen,
    forgetBaseline,
    refetchCounts: refetch,
  };
};
